import React, { FC, useEffect, useMemo, useState } from "react";
import { useGetFpbSummaries } from "../../../../../services/vod/Query";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import SearchTextInput from "../../../../../components/themed/SearchTextInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faChevronUp,
    faDownload,
    faEye,
    faLink,
    faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import { FpbSummary } from "../../../../../models/vod/Models";
import VODFpbSummaryView from "../VODFpbSummaryView";
import Modal from "../../../../../context/modal/Modal";
import VODAdminLayout from "../../../../../layouts/admin/VODAdminLayout";
import FpbSummaryModal from "../form/FpbSummaryModal";

const VODFpbSummaryListingView: FC = () => {
    const [ searchText, setSearchText ] = useState("");
    const [ debouncedSearch, setDebouncedSearch ] = useState("");
    const [ sorting, setSorting ] = React.useState([ { id: "lastModifiedDateTime", desc: true } ]);
    const [ pagination, setPagination ] = React.useState({ pageIndex: 0, pageSize: 20 });

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 350);
        return () => clearTimeout(t);
    }, [ searchText ]);

    const [ selectedRow, setSelectedRow ] = useState<FpbSummary>();
    const [ openViewModal, setOpenViewModal ] = React.useState(false);
    const [ openEditModal, setOpenEditModal ] = React.useState(false);

    const {
        data: fpbSummaryEntityCollection,
        isLoading,
        isFetching,
    } = useGetFpbSummaries({
        search: debouncedSearch,
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortField: sorting[0].id,
        sortDirection: sorting[0].desc ? "DESC" : "ASC",
    });

    const openFpbModal = (
        e: React.MouseEvent<HTMLElement, MouseEvent>,
        fpbSummary: FpbSummary,
        setOpenModal: (openModal: boolean) => void,
    ) => {
        e.stopPropagation();
        setOpenModal(true);
        setSelectedRow(fpbSummary);
    };

    const closeFpbModal = (
        setOpenModal: (openModal: boolean) => void
    ) => {
        setSelectedRow(undefined);
        setOpenModal(false);
    };

    const columns: ColumnDef<FpbSummary>[] = useMemo(
        () => [
            {
                id: "contentPartnerId",
                header: "Partner Id",
                accessorKey: "contentPartnerId",
                size: 70,
            },
            {
                id: "contentPartnerName",
                header: "Partner Name",
                accessorKey: "contentPartnerName",
                size: 120,
            },
            {
                id: "title",
                header: "Title",
                accessorKey: "title",
            },
            {
                id :"seasonNumberAndEpisodeNumber",
                header: "Item Code",
                accessorKey: "seasonNumberAndEpisodeNumber",
                size: 70,
            },
            {
                id :"certificateReferenceNumber",
                header: "Certificate Number",
                accessorKey: "certificateReferenceNumber",
                size: 100,
            },
            {
                id: "certificateLocation",
                header: "Certificate",
                cell: ({ cell, row }) =>
                    row.original.certificateLocation ? (
                        <div className={`h-full flex justify-center items-center`}>
                            <a
                                onClick={(e) => e.stopPropagation()}
                                key={`${cell.id}-${row.id}`}
                                href = {`${row.original.certificateLocation}`} target={"_blank"} rel="noreferrer"
                            >
                                <FontAwesomeIcon icon={faLink} size="1x" className="text-brand-300"/>
                            </a>
                        </div>
                ) : <></>,
                size: 40,
            },
            {
                id: "row-action-buttons",
                cell: ({ cell, row }) => (
                    <div key={`${cell.id}-${row.id}`} className={`flex gap-4 justify-center`}>
                        <button
                            onClick={(e) => openFpbModal(e, row.original, setOpenEditModal)}
                            data-tooltip-content="Edit FPB Summary"
                            data-tooltip-id="global-tooltip"
                        >
                            <FontAwesomeIcon icon={faPenToSquare} size="1x" className="text-brand-300"/>
                        </button>
                        <div key={`${cell.id}-${row.id}`}>
                            <button
                                data-tooltip-content="View FPB Summary"
                                data-tooltip-id="global-tooltip"
                                onClick={(e) => openFpbModal(e, row.original, setOpenViewModal)}
                            >
                                <FontAwesomeIcon icon={faEye} size="1x" className="text-brand-300"/>
                            </button>
                        </div>
                    </div>
                ),
                size: 80,
            },
        ],
        []
    );

    const table = useReactTable({
        columns,
        data: fpbSummaryEntityCollection?.items ?? [],
        state: {
            sorting,
            pagination,
        },
        manualSorting: true,
        manualFiltering: true,
        manualPagination: true,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <VODAdminLayout heading="VOD FPB Summaries">
            <div className="flex flex-col gap-3 h-full min-h-0 p-4 max-w-12xl mx-auto">
                <div className="flex flex-row gap-3 items-center justify-between">
                    <div className="flex gap-2 flex-1 min-w-0">
                        <h2>FPB Summaries</h2>
                        <a
                            className = {`flex flex-row gap-2 items-center shadow-md`}
                            href="/api/vod/fpb-summary/export/csv"
                            download
                            data-tooltip-content="FPB Summary CSV Export"
                            data-tooltip-id="global-tooltip"
                        >
                            <FontAwesomeIcon icon={faDownload} size="1x" className="text-brand-300" />
                        </a>
                    </div>
                    <div>
                        <SearchTextInput
                            value={searchText}
                            placeholder="Search"
                            handleUpdate={setSearchText}
                        />
                    </div>
                </div>

                <div className="flex-1 min-h-0">
                    <div className="h-full overflow-y-auto">
                        <table className="w-full table-fixed">
                            <thead className="sticky top-0 z-10 text-white dark:text-white forced-colors:text-[HighlightText] bg-gray-800 dark:bg-gray-800 forced-colors:bg-[Highlight]">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th key={header.id} className="sticky top-0 z-10 border-b" style={{ width: `${header.getSize()}px` }}>
                                            {header.isPlaceholder ? null : (
                                                <div
                                                    role="button"
                                                    onClick={() => header.column.toggleSorting(header.column.getIsSorted() === "asc")}
                                                    style={{ cursor: "pointer", userSelect: "none" }}
                                                    className={
                                                        header.column.getIsSorted()
                                                        ? (header.column.getIsSorted() === "asc"
                                                          ? "px-4 py-3 text-left border-t-2 border-brand-500 dark:border-brand-400"
                                                          : "px-4 py-3 text-left border-b-2 border-brand-500 dark:border-brand-400")
                                                        : "px-4 py-3 text-left border-0"}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {header.column.getIsSorted()
                                                        ? (header.column.getIsSorted() === "asc"
                                                            ? <FontAwesomeIcon icon={faChevronUp} size="1x" className="ml-4" />
                                                            : <FontAwesomeIcon icon={faChevronDown} size="1x" className="ml-4" />)
                                                        : <></>}
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                            </thead>
                            <tbody>
                            {isFetching && !fpbSummaryEntityCollection ? (
                                <tr>
                                    <td colSpan={columns.length} className="p-4 text-center">
                                        Fetching...
                                    </td>
                                </tr>
                            ) : isLoading && !fpbSummaryEntityCollection ? (
                                <tr>
                                    <td colSpan={columns.length} className="p-4 text-center">
                                        Loading...
                                    </td>
                                </tr>
                            ) : fpbSummaryEntityCollection?.items?.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="border-t hover:bg-brand-400 hover:text-white cursor-pointer font-mono"
                                        role="link"
                                        onClick={(e) => openFpbModal(e, row.original, setOpenViewModal)}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id} className="p-2 align-top truncate px-4" style={{ width: `${cell.column.getSize()}px` }}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} className="p-4 text-center">
                                        No results
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-3 flex flex-row items-center justify-between text-sm">
                    <div>
                        <strong>{fpbSummaryEntityCollection?.total ?? 0}</strong>
                        {" "}
                        Items
                    </div>

                    <div className="flex flex-row items-center gap-3">
                        <button
                            className="px-3 py-1 disabled:text-gray-600"
                            onClick={() => setPagination((prevPagination) => {
                                return { ...prevPagination, pageIndex: 0 };
                            })}
                            disabled={(!fpbSummaryEntityCollection?.total) || (pagination.pageIndex === 0)}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} size="1x" />
                            <FontAwesomeIcon icon={faChevronLeft} size="1x" />
                        </button>

                        <button
                            className="px-3 py-1 disabled:text-gray-600"
                            onClick={() =>
                                setPagination((prevPagination) => {
                                    const prevPageIndex = prevPagination.pageIndex;
                                    return { ...prevPagination, pageIndex: Math.max(0, prevPageIndex - 1) };
                                })
                            }
                            disabled={(!fpbSummaryEntityCollection?.total) || (pagination.pageIndex === 0)}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} size="1x" />
                        </button>

                        <span className="text-sm">
                            Page
                            {" "}
                            <strong>{pagination.pageIndex + 1}</strong>
                            {" "}
                            of
                            {" "}
                            <strong>{Math.max(1, Math.ceil((fpbSummaryEntityCollection?.total ?? 0) / pagination.pageSize))}</strong>
                        </span>

                        <button
                            className="px-3 py-1 disabled:text-gray-600"
                            onClick={() =>
                                setPagination((prevPagination) => {
                                    const total = fpbSummaryEntityCollection?.total ?? 0;
                                    const prevPageIndex = prevPagination.pageIndex;
                                    const pageSize = prevPagination.pageSize;
                                    return { ...prevPagination, pageIndex: (prevPageIndex + 1 < Math.ceil(total / pageSize) ? prevPageIndex + 1 : prevPageIndex) };
                                })
                            }
                            disabled={(!fpbSummaryEntityCollection?.total) || ((pagination.pageIndex + 1) * pagination.pageSize >= fpbSummaryEntityCollection.total)}
                        >
                            <FontAwesomeIcon icon={faChevronRight} size="1x" />
                        </button>

                        <button
                            className="px-3 py-1 disabled:text-gray-600"
                            onClick={() =>
                                setPagination((prevPagination) => {
                                    const total = fpbSummaryEntityCollection?.total ?? 0;
                                    const pageSize = prevPagination.pageSize;
                                    return { ...prevPagination, pageIndex: (Math.ceil(total / pageSize) - 1) };
                                })
                            }
                            disabled={(!fpbSummaryEntityCollection?.total) || ((pagination.pageIndex + 1) * pagination.pageSize >= fpbSummaryEntityCollection.total)}
                        >
                            <FontAwesomeIcon icon={faChevronRight} size="1x" />
                            <FontAwesomeIcon icon={faChevronRight} size="1x" />
                        </button>
                    </div>

                    <div className="flex flex-row items-center gap-2">
                        <label>Page Size</label>
                        <select
                            value={pagination.pageSize}
                            onChange={(e) =>
                                setPagination({
                                    pageIndex: 0,
                                    pageSize: Number(e.target.value),
                                })
                            }
                            className="border px-2 py-1 rounded bg-light dark:bg-brand-700"
                        >
                            <option className={pagination.pageSize === 10 ? "bg-brand-400" : "bg-light dark:bg-brand-700"} value={10}>10</option>
                            <option className={pagination.pageSize === 20 ? "bg-brand-400" : "bg-light dark:bg-brand-700"} value={20}>20</option>
                            <option className={pagination.pageSize === 50 ? "bg-brand-400" : "bg-light dark:bg-brand-700"} value={50}>50</option>
                            <option className={pagination.pageSize === 100 ? "bg-brand-400" : "bg-light dark:bg-brand-700"} value={100}>100</option>
                        </select>
                    </div>
                </div>
            </div>

            {
                selectedRow && (
                <>
                    <Modal
                        modalId={`view-modal-fpb`}
                        modalTitle={"FPB Summary"}
                        openModal={openViewModal}
                        onClose={() => closeFpbModal(setOpenViewModal)}
                    >
                        <Modal.Body>
                            <VODFpbSummaryView fpbSummary={selectedRow} />
                        </Modal.Body>
                    </Modal>
                    <FpbSummaryModal
                        openModal={openEditModal}
                        onClose={() => closeFpbModal(setOpenEditModal)}
                        key={selectedRow.assetId}
                        modalId={"edit-modal-fpb"}
                        content={selectedRow}
                    />
                </>
            )}
        </VODAdminLayout>
    );
};

export default VODFpbSummaryListingView;